import { useEffect, useState } from 'react'
import cn from 'classnames'

import ButtonBlue from 'components/ui/button/_color/_blue'
import Popup from 'components/ui/popup'
import InputAddress from 'components/app/input-address'
import InputTag from 'components/app/input-tag'

import { useAppDispatch, useAppSelector } from 'store/hooks'
import { fetchCurrencies, getCurrencies } from 'store/reducers/currencies'
import { ICurrency } from 'store/fetchers/currencies'
import {
  EMessageTypes,
  sendVerificationCodeRateLimited
} from 'store/fetchers/utils'

import { SelectCurrency } from 'pages/account/settings/components/white-list/components/add-address-popup/components/select-currency'

import { _whitelistWallets } from 'store/fetchers/wallets'
import { selectedUser } from 'store/reducers/users'

import { useTranslation } from 'utils/use-translation'

import CodeVerificationInput from 'components/global/code-verification-input'

import { TIMER_RESEND } from 'config/constants'

import useViewport from 'libs/use-viewport'

import { ReactSVG } from 'react-svg'

import styles from '../../styles.module.scss'

import Input from '../../../../../../../components/app/input'

import SuccessIcon from '../../icons/success.svg'

import { Confirm } from './components/confirm'
import ConfirmData from './components/confirm-data'

import { getSubTitle, getTitle } from './utils'
import { useAddressValidationByNetworkAndCode } from 'utils/use-address-validation'

export const AddAddressPopup = ({ setPopupIsVisible, refetch, openPopup }) => {
  const { t, replacePlaceholders } = useTranslation()

  const [view] = useViewport()
  const isMobile = ['xs-mobile', 'mobile'].includes(view)

  const dispatch = useAppDispatch()
  const list = useAppSelector(getCurrencies)
  const user = useAppSelector(selectedUser)
  
  const [step, setStep] = useState<1 | 2 | 3 | 4>(1)
  const [coin, setCoin] = useState<ICurrency>()
  const [address, setAddress] = useState('')
  const [extraId, setExtraId] = useState('')
  const [addressName, setAddressName] = useState<string>('')

  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isValidating, setIsValidating] = useState(false)
  const [addressNameErrorMessage, setAddressNameErrorMessage] = useState<string>('')
  const [code2Fa, setCode2Fa] = useState('')
  const [isValid2faCode, setIsValid2faCode] = useState<boolean>(null)
  const [verificationToken, setVerificationToken] = useState('')
  const [errorCode, setErrorCode] = useState('')
  const [submitError, setSubmitError] = useState('')

  const {
    isValidAddress,
    isValidExtraId,
    isMemoRequired,
    addressErrorMessage,
    extraIdErrorMessage,
    placeHolderMessage,
    warningMessage,
  } = useAddressValidationByNetworkAndCode({
    address,
    extraId,
    currencyInfo: coin,
    setIsValidating,
  })

  useEffect(() => {
    if (!list.length) dispatch(fetchCurrencies(false))
  }, [list])

  useEffect(() => {
    if (!coin?.is_extra_id_exists) {
      setExtraId('')
    }
  }, [coin])

  useEffect(() => {
    if (!coin && list?.length) {
      const inititalCoin = list
        .filter((i) => i.is_allowed_for_wallet)
        .sort((a, b) => {
          if (a.exchange_priority === null) return 1
          if (b.exchange_priority === null) return -1

          return a.exchange_priority - b.exchange_priority
        })[0]

      setCoin(inititalCoin)
    }
  }, [coin, list])

  const sendVerificationCode = async () => {
    setIsLoading(true)

    try {
      const token = await sendVerificationCodeRateLimited({
        type: EMessageTypes.CONFIRM_WHITELIST_ADDING,
        overwrite: {
          ticker: coin?.code,
          network: coin?.network,
          address: address,
          tag: coin?.is_extra_id_exists && extraId ? extraId?.trim() : ' - ',
          address_name: addressName || ' - ',
        },
      })

      setVerificationToken(token.tokens[0])
      setStep(2)
    } catch (err) {
      const msg = err?.props?.response?.error || err?.response?.message || err?.response?.error || ''
      if (msg?.includes('limit')) {
        setSubmitError(t('rate_limit_exceeded_retry_later'))
        setTimeout(() => setSubmitError(''), 10000)
      }
    } finally {
      setIsLoading(false)
    }
  }

  const onClose = () => {
    setAddress('')
    setExtraId('')
    setCoin(undefined)
    setPopupIsVisible(false)
  }

  const addressNameChange = (event) => {
    setAddressNameErrorMessage('')

    const regexPattern = /^[a-zA-Z0-9\s]+$/
  
    if (event.target.value.length > 30) {
      setAddressNameErrorMessage(t('address_character_number_error'))
      return
    }

    if (!regexPattern.test(event.target.value) && event.target.value.length !== 0) {
      setAddressNameErrorMessage(t('only_use_letter_spaces_error'))
    }

    setAddressName(event.target.value)
  }

  async function onCode2faSubmit(code2fa) {
    setIsLoading(true)

    const res: any = await _whitelistWallets({
      code: coin?.code,
      network: coin?.network,
      address: address?.trim(),
      code_2fa: code2fa,
      verification_token: verificationToken,
      tag: coin?.is_extra_id_exists && extraId ? extraId.trim() : '',
      address_name: addressName ?? null,
    })

    setIsLoading(false)
    setIsValid2faCode(res?.result)

    if (res?.result) {
      setStep(4)
      setErrorCode('')
      refetch()
    }

    if (res?.message) {
      setErrorCode(
        res?.message?.includes('failed') ? t('rate_limit_exceeded_retry_later') :
          res?.code === 'INVALID_REQUEST_PARAMS' 
            ? t('code_invalid_try_again_later') 
            : res?.message)
    }
  }

  return (
    <Popup
      onClose={onClose}
      heading={t(getTitle(step, isMobile))}
      subtitle={t(getSubTitle(step, isMobile))}
      className={styles.whitelist}
      mainClassName={styles.whitelist__main}
      titleClassName={cn(styles.whitelist__title, { [styles['whitelist__title-success']]: step === 4 })}
      subtitleClassName={styles.whitelist__subtitle}
      crossIconBtnStyle={styles['whitelist__close-btn']}
      hasBackBtn={step === 2 || step === 3}
      onBack={() =>
        setStep((prev) => {
          const next = prev - 1

          return (next >= 1 && next <= 4 ? next : 1) as 1 | 2 | 3 | 4
        })
      }
    >
      {step === 1 ? (
        user && user?.isActive2Fa ? (
          <section className={styles['whitelist__init-container']}>
            <div className={cn(styles.whitelist__row, styles.whitelist__init)}>
              <div className={cn(styles.whitelist__column, styles.whitelist__coin)}>
                <SelectCurrency
                  coin={coin}
                  setCoin={setCoin}
                  className={styles['whitelist__coin-main']}
                  logoClassname={styles['whitelist__coin-logo']}
                  selectedClassaname={styles['whitelist__coin-select']}
                  labelClassname={styles.whitelist__label}
                  tickerClassname={styles['whitelist__coin-ticker']}
                />
              </div>

              <div className={cn(styles.whitelist__column, styles.column_relative)}>
                <div className={styles.whitelist__label}>{t('address_name')}</div>
                <Input
                  className={cn(styles.input, {
                    [styles['whitelist__input-error']]: addressNameErrorMessage,
                  })}
                  type="text"
                  onChange={addressNameChange}
                  value={addressName}
                  placeholder={t('address_name')}
                />

                <small className={styles.error}>{addressNameErrorMessage}</small>
              </div>
            </div>

            <div className={cn(styles.whitelist__row, styles['whitelist__second-row'])}>
              <div
                className={cn(styles.whitelist__column, {
                  [styles['whitelist__input-error']]: addressErrorMessage && address,
                })}
              >
                <div className={styles.whitelist__label}>{t('address')}</div>
                <InputAddress
                  currency={coin}
                  value={address}
                  isValid={!Boolean(addressErrorMessage) && isValidAddress && !!address}
                  onChange={setAddress}
                  errorMessage={addressErrorMessage}
                  isVisibleError={Boolean(addressErrorMessage) && !isValidAddress && !!address}
                  isVisibleWarning={true}
                  warningMessage={warningMessage}
                  showCross={false}
                  errorClassname={styles.whitelist__err}
                  placeholder={t('address')}
                />
              </div>

              {coin?.is_extra_id_exists && (
                <div
                  className={cn(styles.whitelist__column, styles.tag, {
                    [styles['whitelist__input-error']]: extraIdErrorMessage && extraId,
                  })}
                >
                  <div className={styles.whitelist__label}>{t('tag_subtitle')}</div>
                  <InputTag
                    currency={coin}
                    value={extraId}
                    onChange={setExtraId}
                    isValid={!Boolean(extraIdErrorMessage) && isValidExtraId && !!extraId}
                    isVisibleErrors={Boolean(extraIdErrorMessage) && !isValidExtraId && !!extraId}
                    errorMessage={extraIdErrorMessage}
                    placeholder={placeHolderMessage || t('tag_subtitle')}
                    showCross={false}
                    errorClassname={styles.whitelist__err}
                  />
                </div>
              )}
            </div>

            <div className={styles.whitelist__footer}>
              <div className={styles.buttons}>
                {submitError && <span className={styles.error}>{submitError}</span>}
                
                <ButtonBlue
                  disableHover={true}
                  isLoading={isLoading}
                  disabled={Boolean(
                    !address ||
                    !!addressErrorMessage ||
                    !!addressNameErrorMessage ||
                    !!extraIdErrorMessage ||
                    isLoading || isValidating ||
                    isMemoRequired && !extraId
                  )}
                  className={styles.addAddressPopupFooter_buttons_button}
                  onClick={sendVerificationCode}
                >
                  {t('submit')}
                </ButtonBlue>
              </div>
            </div>
          </section>
        ) : (
          <p
            onClick={() => {
              openPopup()
              setPopupIsVisible(false)
            }}
            className={styles.infoWarning}
          >
            {t('enable_2fa')}
          </p>
        )
      ) : step === 2 ? (
        <Confirm
          errorCode={errorCode}
          setErrorCode={setErrorCode}
          address={address}
          extraId={extraId}
          currencyInfo={coin}
          verificationToken={verificationToken}
          setVerificationToken={setVerificationToken}
          handleNext={(token) => {
            setVerificationToken(token)
            setStep(3)
          }}
          onBack={() => setStep(1)}
        />
      ) : step === 3 ? (
        <>
          <ConfirmData currencyInfo={coin} address={address} hasTag={Boolean(coin?.is_extra_id_exists)} tag={extraId} />

          <section>
            <h2 className={styles.verification__step}>
              {t('verification')} <span>2/2</span>
            </h2>
            <p className={styles.verification__notification}>
              {replacePlaceholders(t('enter_tfa_code'), {
                codeLength: 6,
              })}
            </p>

            <CodeVerificationInput
              code={code2Fa}
              setCode={setCode2Fa}
              isCodeValid={isValid2faCode}
              setIsCodeValid={setIsValid2faCode}
              errorMsg={errorCode}
              setErrorMsg={setErrorCode}
              isLoading={isLoading}
              countSeconds={TIMER_RESEND}
              verifyCode={onCode2faSubmit}
              showError
            />
          </section>
        </>
      ) : (
        <>
          <ConfirmData currencyInfo={coin} address={address} hasTag={Boolean(coin?.is_extra_id_exists)} tag={extraId} />

          <div className={styles.whitelist__footer}>
            <div className={styles.buttons}>
              <ButtonBlue onClick={onClose}>{t('great')}</ButtonBlue>
            </div>
          </div>

          <ReactSVG className={styles['success-icon']} src={SuccessIcon} width={26} height={26} />
        </>
      )}
    </Popup>
  )
}
